import type { ProductData } from '@appcharge/shared-ui';

import { EPopupSubType } from '@/lib/enums';
import { OfferUI } from '@/lib/types';

export interface OfferForMixpanel {
  offerId: string;
  offerName: string;
  publisherOfferId?: string;
  offerType?: string;
  indexToCollect?: number;
  subType?: string;
  priority?: number;
  isAllowedToCollect?: boolean | null;
  displayRule?: { frequency?: number; cooldownInMinutes?: number };
  productSale?: { sale?: number | null };
  schedule?: { timeFrames: Array<{ startTime: Date | string; endTime: Date | string }> };
  rewardCalendar?: { unlockedIndex: number; nextUnlockAt: Date | string };
  offerUi?: OfferUI;
  productsSequence?: Array<{
    products: ProductData[];
    prices: { priceInUsdCents: number };
  }>;
}

export type EOfferState = 'collectable' | 'locked' | null;

export type ETriggerName = 'PostPurchase' | 'StoreRefresh' | 'UserClick' | 'TimerEnded';

export const computeTriggerName = (offer: OfferForMixpanel): ETriggerName => {
  if (offer.subType === EPopupSubType.POST_PURCHASE) return 'PostPurchase';
  if (offer.subType === EPopupSubType.STORE_REFRESH) return 'StoreRefresh';
  if (
    offer.subType === EPopupSubType.DAILY_BONUS ||
    offer.subType === EPopupSubType.REWARD_CALENDAR
  )
    return 'TimerEnded';
  return 'UserClick';
};

export const computeOfferState = (offer: OfferForMixpanel): EOfferState => {
  if (offer.isAllowedToCollect == null) return null;
  return offer.isAllowedToCollect ? 'collectable' : 'locked';
};

export const computeTimeLeftSec = (offer: OfferForMixpanel): number | null => {
  if (offer.subType !== EPopupSubType.REWARD_CALENDAR || !offer.rewardCalendar?.nextUnlockAt) {
    return null;
  }
  const endMs = new Date(offer.rewardCalendar.nextUnlockAt).getTime();
  if (!Number.isFinite(endMs)) return null;
  return Math.max(0, Math.floor((endMs - Date.now()) / 1000));
};

export const computeBadgeInd = (offer: OfferForMixpanel): boolean => {
  const subType = offer.subType;
  const hasBadgeImage = Boolean(offer.offerUi?.badgeCoverImage);
  const isBadgeSubType =
    subType === EPopupSubType.DAILY_BONUS ||
    subType === EPopupSubType.STORE_REFRESH ||
    subType === EPopupSubType.REWARD_CALENDAR;
  return hasBadgeImage && isBadgeSubType;
};

export const arrangeMixpanelOfferData = (data: OfferForMixpanel[]) => {
  return data.map((offer) => {
    const isRewardCalendar = offer.subType === EPopupSubType.REWARD_CALENDAR;
    const activeIndex = offer.indexToCollect ? offer.indexToCollect - 1 : 0;
    return {
      offer_id: offer.offerId,
      external_id: offer.publisherOfferId,
      name: offer.offerName,
      sale_percentage: offer.productSale?.sale,
      type: offer.offerType,
      subType: offer.subType ?? null,
      priority: offer.priority ?? null,
      index: isRewardCalendar
        ? offer.rewardCalendar != null
          ? offer.rewardCalendar.unlockedIndex + 1
          : null
        : (offer.indexToCollect ?? null),
      state: computeOfferState(offer),
      sub_type: offer.subType ?? null,
      start_date: offer.schedule?.timeFrames[0]?.startTime,
      end_date: offer.schedule?.timeFrames[0]?.endTime,
      expiration_date: isRewardCalendar ? (offer.rewardCalendar?.nextUnlockAt ?? null) : null,
      time_left: computeTimeLeftSec(offer),
      products:
        offer.productsSequence?.[activeIndex]?.products?.map((product: ProductData) => ({
          product_id: product.publisherProductId,
          product_name: product.name,
          product_type: product.productType,
          product_quantity: product.quantity,
        })) ?? [],
      priceInUsdCents: offer.productsSequence?.[activeIndex]?.prices?.priceInUsdCents ?? 0,
      trigger_name: computeTriggerName(offer),
      trigger_frequency: offer.displayRule?.frequency ?? null,
      has_badge: computeBadgeInd(offer),
    };
  });
};
